import type {
  EncodedKeyframe,
  EncodedKeyframeEffectOptions,
  EncodedOptionalEffectTiming,
  FsId,
  RawEventOfKind,
} from '@fullstory/recording-api';
import { ImmutableTimeline, Timeline } from './timeline/timeline';

export class KeyframeEffectEventTimeline {
  private _composite = new Timeline<CompositeOperation>();
  public get composite(): ImmutableTimeline<CompositeOperation> {
    return this._composite;
  }

  private _keyframes = new Timeline<Keyframe[] | null>();
  public get keyframes(): ImmutableTimeline<Keyframe[] | null> {
    return this._keyframes;
  }

  private _iterationComposite = new Timeline<IterationCompositeOperation>();
  public get iterationComposite(): ImmutableTimeline<IterationCompositeOperation> {
    return this._iterationComposite;
  }

  private _targetId = new Timeline<FsId | null>();
  public get targetId(): ImmutableTimeline<FsId | null> {
    return this._targetId;
  }

  private _timing = new Timeline<OptionalEffectTiming>();
  public get timing(): ImmutableTimeline<OptionalEffectTiming> {
    return this._timing;
  }

  constructor() {
    const start = Number.NEGATIVE_INFINITY;
    const end = Number.POSITIVE_INFINITY;

    this._composite.add('replace', start, end);
    this._iterationComposite.add('replace', start, end);
  }

  addCreatedEvent(event: RawEventOfKind<'KEYFRAME_EFFECT_CREATED'>) {
    const [, targetId, encodedKeyframes, encodedOptions] = event.Args;
    const keyframes = this._decodeKeyframes(encodedKeyframes);
    const options = this._decodeKeyframeEffectOptions(encodedOptions);
    const { composite, iterationComposite, ...timing } = options;

    this._keyframes.addEventWithValue(event, keyframes);
    this._targetId.addEventWithValue(event, targetId);

    if (composite) {
      this._composite.addEventWithValue(event, composite);
    }

    if (iterationComposite) {
      this._iterationComposite.addEventWithValue(event, iterationComposite);
    }

    if (Object.keys(timing).length > 0) {
      this._timing.addEventWithValue(event, timing);
    }
  }

  addMethodCalledEvent(event: RawEventOfKind<'KEYFRAME_EFFECT_METHOD_CALLED'>) {
    const methodData = event.Args[1];

    switch (methodData[0]) {
      case 'setKeyframes': {
        const keyframes = this._decodeKeyframes(methodData[1][0]);
        this._keyframes.addEventWithValue(event, keyframes);
        break;
      }
      case 'updateTiming': {
        const timing = this._decodeOptionalEffectTiming(methodData[1][0]);
        this._timing.addEventWithValue(event, timing);
        break;
      }
    }
  }

  addPropertySetEvent(event: RawEventOfKind<'KEYFRAME_EFFECT_PROPERTY_SET'>) {
    const methodData = event.Args[1];

    // TODO (scottnorvell): add support for 'pseudoElement', 'composite', and 'iterationComposite'
    switch (methodData[0]) {
      case 'target': {
        this._targetId.addEventWithValue(event, methodData[1]);
        break;
      }
    }
  }

  private _decodeKeyframeEffectOptions(
    options?: EncodedKeyframeEffectOptions,
  ): KeyframeEffectOptions {
    if (!options) return {};

    const [
      delay,
      direction,
      duration,
      easing,
      endDelay,
      fill,
      iterationStart,
      iterations,
      composite,
      iterationComposite,
    ] = options;

    return {
      delay,
      direction,
      duration,
      easing,
      endDelay,
      fill,
      iterationStart,
      iterations: normalizeIterations(iterations),
      composite,
      iterationComposite,
    };
  }

  private _decodeKeyframes(keyframes: EncodedKeyframe[]): Keyframe[] {
    return keyframes.map(([properties, composite, easing, offset]) => {
      const decoded = Object.fromEntries(properties);

      /**
       * README(scottnorvell):
       *   A note on the verbosity of this: We're going to call effect.setKeyframes() with
       *   this object. If any values are null, .setKeyframes() throws an error. 😞
       */
      if (composite != null) {
        decoded.composite = composite;
      }
      if (easing != null) {
        decoded.easing = easing;
      }
      if (offset != null) {
        decoded.offset = offset;
      }
      return decoded;
    });
  }

  private _decodeOptionalEffectTiming(timing?: EncodedOptionalEffectTiming): OptionalEffectTiming {
    const decoded: OptionalEffectTiming = {};
    if (!timing) return decoded;

    const [delay, direction, duration, easing, endDelay, fill, iterationStart, _iterations] =
      timing;

    const iterations = normalizeIterations(_iterations);

    /**
     * README(scottnorvell):
     *   A note on the verbosity of this: We're going to call effect.updateTiming() with
     *   this object. If any values are null, .updateTiming() throws an error. 😞
     */
    if (iterations != null) {
      decoded.iterations = iterations;
    }

    if (delay != null) {
      decoded.delay = delay;
    }

    if (direction != null) {
      decoded.direction = direction;
    }

    if (duration != null) {
      decoded.duration = duration;
    }

    if (easing != null) {
      decoded.easing = easing;
    }

    if (endDelay != null) {
      decoded.endDelay = endDelay;
    }

    if (fill != null) {
      decoded.fill = fill;
    }

    if (iterationStart != null) {
      decoded.iterationStart = iterationStart;
    }

    return decoded;
  }
}

function normalizeIterations(iterations: number | string | undefined): number | undefined {
  if (typeof iterations === 'undefined') return iterations;
  if (typeof iterations === 'number') return iterations;

  const count = Number(iterations);
  return Number.isNaN(count) ? undefined : count;
}

// avoid the stronger typedef from EffectTiming
interface KeyframeEffectOptions extends OptionalEffectTiming {
  composite?: CompositeOperation;
  iterationComposite?: IterationCompositeOperation;
  pseudoElement?: string | null;
}
